import { joinWithAnd, useRouter } from '@application/utils';
import type { BreederRelatedLinksGetBreedersQuery } from '@gql/graphql';
import type { RelatedLinksCategory, RelatedLinksData } from '@presentation/components';
import { isEmpty } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useGetArticles } from './useGetArticles';
import { useGetBreeders } from './useGetBreeders';
import { useGetBreeds } from './useGetBreeds';

export const useRelatedLinks = (breedIds?: number[]) => {
  const { translate, routeLocationDescriptor } = useRouter();
  const { articles, articlesLoading } = useGetArticles();
  const { breeds, breedsLoading } = useGetBreeds();
  const { breeders, breedersLoading } = useGetBreeders(breedIds);

  const formattedArticles = useMemo((): RelatedLinksCategory | undefined => {
    if (articlesLoading) return;
    if (!articles?.length) return;
    const foundArticles = articles.filter(
      (x) => x.translations?.[0]?.title && x.translations?.[0]?.url,
    );
    if (!foundArticles.length) return;
    return {
      label: translate('common.sections.relatedLinks.articles.title'),
      items: foundArticles.map((x) => ({
        label: x.translations[0].title,
        link: routeLocationDescriptor('article', { params: [x.translations[0].url] }),
      })),
    };
  }, [articles, articlesLoading, routeLocationDescriptor, translate]);

  const formattedBreeds = useMemo((): RelatedLinksCategory | undefined => {
    if (breedsLoading) return;
    if (!breeds.length) return;
    const foundBreeeds = breeds.filter(
      (x) => x.translations?.[0]?.name && x.translations?.[0]?.url,
    );
    if (!foundBreeeds.length) return;
    return {
      label: translate('common.sections.relatedLinks.breeds.titleOthers'),
      items: foundBreeeds.map((x) => ({
        label: translate('common.sections.relatedLinks.breeds.link').replace(
          '$breed',
          x.translations[0].name as string,
        ),
        link: routeLocationDescriptor('breed', { params: [x.translations[0].url as string] }),
      })),
    };
  }, [breeds, breedsLoading, routeLocationDescriptor, translate]);

  const getBreedNames = useCallback(
    (breeder: NonNullable<BreederRelatedLinksGetBreedersQuery['findManyDogBreeder']>[number]) => {
      return breeder?.breederBreeds
        .map(({ dogBreed }) => {
          return dogBreed?.translations?.find((x) => x.name)?.name ?? undefined;
        })
        .filter((x) => x != null);
    },
    [],
  );

  const formattedBreeders = useMemo((): RelatedLinksCategory | undefined => {
    if (breedersLoading) return;
    if (!breeders.length) return;
    const foundBreeeders = breeders.filter(
      (x) =>
        x.breederBreeds?.flatMap((y) => y.dogBreed.translations).filter((z) => z.name).length &&
        x.translations?.[0]?.url,
    );
    if (!foundBreeeders.length) return;
    return {
      label: translate('common.sections.relatedLinks.breeders.title'),
      items: foundBreeeders.map((x) => ({
        label: translate('common.sections.relatedLinks.breeders.link').replace(
          '$breeds',
          joinWithAnd(translate, getBreedNames(x) as string[]),
        ),
        link: routeLocationDescriptor('breeder', { params: [x.translations[0].url as string] }),
      })),
    };
  }, [breeders, breedersLoading, getBreedNames, routeLocationDescriptor, translate]);

  const relatedLinks = useMemo((): RelatedLinksData | undefined => {
    const links = {
      categories: [
        ...(formattedBreeders ? [formattedBreeders] : []),
        ...(formattedBreeds ? [formattedBreeds] : []),
        ...(formattedArticles ? [formattedArticles] : []),
      ].filter((x) => !!x),
    };
    if (isEmpty(links.categories)) return;
    return links;
  }, [formattedArticles, formattedBreeders, formattedBreeds]);

  return { relatedLinks };
};
